import React, { useState } from 'react';
import QMTable, { QMHeaderRow, QMDataCell, QMDataRow } from 'corp/components/QM/QMTable';
import { Checkbox, IconButton } from 'react-scale/components/design-system';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { copyToClipboard } from '@scale/frontend-lib/clipboard';
import QMTableFooter from 'react-scale/corp/components/QM/QMTableFooter';
import { QMBenchmark, PaginationResponse } from '@scale/shared/qm/qmRequestTypes';
import { makeStyles } from 'react-scale/components/design-system/styles';
import { Link } from 'react-router-dom';
import QMBenchmarksEditAllModal from './QMBenchmarksEditAllModal';
import { BenchmarkAssignmentStatus } from '@scale/shared/defaults';
import { TaskType } from '@scale/shared-types';
import moment from 'moment';
import BenchmarkSuspicion from '../benchmark/BenchmarkSuspicion';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';

const QMBenchmarksTable: React.FC<{
  benchmarks: QMBenchmark[];
  openEditModal: (benchmark: QMBenchmark) => void;
  openReportModal: (benchmark: QMBenchmark) => void;
  loading: boolean;
  pagination: PaginationResponse | undefined;
  loadMoreBenchmarks: () => Promise<void>;
  onUpdate: () => Promise<void>;
  projectId: string;
  taskType: TaskType;
  selectedIds: string[];
  setSelectedIds: (ids: string[]) => void;
}> = ({
  benchmarks,
  openEditModal,
  openReportModal,
  loading,
  pagination,
  loadMoreBenchmarks,
  onUpdate,
  projectId,
  taskType,
  selectedIds,
  setSelectedIds,
}) => {
  const remoAdminContext = useRemoAdminCtx();
  const [showingEditAllModal, setShowingEditAllModal] = useState<boolean>(false);

  const toggleAllCheckboxes = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedIds(benchmarks.map(b => b.id));
    } else {
      setSelectedIds([]);
    }
  };

  const toggleCheckbox = (e: React.ChangeEvent<HTMLInputElement>, benchmark: QMBenchmark) => {
    if (e.target.checked && !selectedIds.includes(benchmark.id)) {
      setSelectedIds([...selectedIds, benchmark.id]);
    } else if (!e.target.checked) {
      setSelectedIds(selectedIds.filter(id => id !== benchmark.id));
    }
  };

  const classes = useStyles();

  const openEditAllModal = () => {
    setShowingEditAllModal(true);
  };

  const closeEditAllModal = () => {
    setShowingEditAllModal(false);
  };

  const copySelectedToClipboard = () => {
    if (selectedIds.length > 0) {
      copyToClipboard(selectedIds.join(', '), { excludeOuterQuotes: true });
    }
  };

  let allowBenchmarkReports = taskType === TaskType.TextCollection;
  const disableBenchmarkReports = useUnleashFeatureFlag(
    'disable-bm-reporting',
    { projectId },
    false,
  );
  allowBenchmarkReports = allowBenchmarkReports && !disableBenchmarkReports;

  return (
    <>
      <QMTable>
        <QMHeaderRow>
          <th>
            <Checkbox
              label={''}
              checked={benchmarks.every(benchmark => selectedIds.includes(benchmark.id))}
              onChange={toggleAllCheckboxes}
            />
          </th>
          <th>
            ID
            <IconButton size="small" data-tip="Copy to clipboard" onClick={copySelectedToClipboard}>
              <FontAwesomeIcon icon="copy" color="lightgrey" />
            </IconButton>
          </th>
          <th>Title</th>
          <th>Status</th>
          <th>Review Levels</th>
          <th>Serve Attempters?</th>
          <th>Benchmark Type</th>
          <th>Attempts</th>
          {allowBenchmarkReports && <th>Reports</th>}
          <th>Created At</th>
          <th></th>
          <th>
            <IconButton size="small" data-tip="Edit status" onClick={openEditAllModal}>
              <FontAwesomeIcon icon="edit" color="lightgrey" />
            </IconButton>
          </th>
        </QMHeaderRow>

        {benchmarks.map(benchmark => (
          <QMDataRow key={benchmark.id}>
            <QMDataCell>
              <Checkbox
                label={''}
                checked={selectedIds.includes(benchmark.id)}
                onChange={e => toggleCheckbox(e, benchmark)}
                value={'checkedBox'}
              />
            </QMDataCell>
            <QMDataCell>
              {remoAdminContext.isRemoAdmin ? (
                remoAdminBenchmarkLink(
                  benchmark.id,
                  benchmark.id.slice(-5),
                  true,
                  classes.monospace,
                )
              ) : (
                <Link to={`/lookup/${benchmark.id}`} className={classes.monospace}>
                  {benchmark.id.slice(-5)}
                </Link>
              )}
              <IconButton
                size="small"
                data-tip="Copy to clipboard"
                onClick={() => copyToClipboard(benchmark.id, { excludeOuterQuotes: true })}
              >
                <FontAwesomeIcon icon="copy" color="lightgrey" />
              </IconButton>
            </QMDataCell>
            <QMDataCell>
              {remoAdminContext.isRemoAdmin ? (
                remoAdminBenchmarkLink(benchmark.id, benchmark.name)
              ) : (
                <Link to={`/qm/queue/${projectId}/benchmark/${benchmark.id}`}>
                  {benchmark.name}
                </Link>
              )}
            </QMDataCell>
            <QMDataCell>
              {benchmark.status === BenchmarkAssignmentStatus.Enabled ? 'Enabled' : 'Disabled'}
            </QMDataCell>
            <QMDataCell>{benchmark.reviewLevels.join(', ')}</QMDataCell>
            <QMDataCell>{benchmark.isAttempterBenchmark ? 'True' : 'False'}</QMDataCell>
            <QMDataCell>
              {benchmark.isInstructiveBenchmark ? 'Instructive' : 'Evaluative'}
            </QMDataCell>
            <QMDataCell>{benchmark.attempts}</QMDataCell>
            {allowBenchmarkReports && (
              <QMDataCell>
                {!!benchmark.reports?.length && (
                  <p>
                    {benchmark.reports?.length}
                    <IconButton
                      size="small"
                      data-tip="Open report table"
                      onClick={() => openReportModal(benchmark)}
                    >
                      <FontAwesomeIcon icon="info-circle" color="lightgrey" />
                    </IconButton>
                  </p>
                )}
              </QMDataCell>
            )}
            <QMDataCell>
              {moment(benchmark.createdAt).utc().format('MM/DD/YYYY HH:mm:ss')}
            </QMDataCell>
            <QMDataCell>
              <BenchmarkSuspicion benchmark={benchmark} />
            </QMDataCell>
            <QMDataCell>
              <IconButton
                size="small"
                data-tip="Edit status"
                onClick={() => openEditModal(benchmark)}
              >
                <FontAwesomeIcon icon="edit" color="lightgrey" />
              </IconButton>
            </QMDataCell>
          </QMDataRow>
        ))}
        {!loading && (
          <QMTableFooter
            currentCount={benchmarks.length}
            totalCount={pagination?.totalCount}
            onLoadMore={loadMoreBenchmarks}
          />
        )}
      </QMTable>
      <QMBenchmarksEditAllModal
        selectedIds={selectedIds}
        showingEditAllModal={showingEditAllModal}
        closeEditAllModal={closeEditAllModal}
        onUpdate={onUpdate}
      />
    </>
  );
};

export const remoAdminBenchmarkLink = (
  benchmarkID: string,
  displayValue?: string,
  isLookup?: boolean,
  className?: string,
) => {
  const url = new URL(window.location.href);
  url.pathname = `/remoadmin/tools/${isLookup ? 'lookup' : 'edit_benchmark'}/${benchmarkID}`;
  return (
    <a className={className} href={url.toString()} target="_blank" rel="noopener noreferrer">
      {displayValue ?? benchmarkID}
    </a>
  );
};

const useStyles = makeStyles({
  monospace: {
    'font-family': 'monospace',
  },
});
export default QMBenchmarksTable;
